<template>
  <MModal
    @close="$emit('close')"
    modal-class="md:max-w-lg"
    inner-class="p-4 lg:p-6 text-14 leading-snug"
  >
    <BackgroundImage
      class="hidden lg:block h-16 max-w-1/3 mx-auto mb-6"
      size="contain"
      :src="provider.logo.sm"
      :alt="provider.displayName"
    />

    <template v-if="bankPayin || bankPayout">
      <p>{{ $t('external_fees.label') }}</p>
      <p class="lead">
        {{ $t('external_fees.international_bank_fees') }}
      </p>
    </template>
    <p v-else class="lead">{{ $t('external_fees.label') }}</p>
    <p class="mt-3" v-html="description" />

    <p class="font-semibold mt-5">{{ $t('external_fees.how_much') }}</p>
    <template v-if="bankPayin || bankPayout">
      <ul :class="{ 'list-disc ml-4': bankPayin && bankPayout }">
        <li
          v-if="bankPayin"
          class="mt-2"
          v-html="$t('external_fees.bank_payin_fee', { payinFee })"
        />
        <li
          v-if="bankPayout"
          class="mt-2"
          v-html="$t('external_fees.bank_payout_fee', { payoutFee, receivedFee })"
        />
      </ul>
      <p class="mt-3">{{ $t('external_fees.bank_fee_note') }}</p>
    </template>
    <template v-else>
      <p class="mt-2" v-html="$t('external_fees.other_fee', { fee: totalFee })" />
      <p class="mt-3">{{ $t('external_fees.other_fee_note') }}</p>
    </template>

    <template v-if="descriptionReason">
      <p class="font-semibold mt-5">
        {{ $t('external_fees.why_this_happens') }}
      </p>
      <p class="mt-1">{{ descriptionReason }}</p>
    </template>
  </MModal>
</template>

<script lang="ts" setup>
import type { Corridor } from '@core/types/imt'
import type { Provider, Quote } from './types'
import { panelOpen } from './analytics/actions'
import { ComparisonSection } from './analytics/objects/ComparisonSection'
import { Comparison } from './analytics/objects/Comparison'
import { ComparisonResult } from './analytics/objects/ComparisonResult'

const props = defineProps<{
  provider: Provider
  quote: Quote
}>()

defineEmits(['close'])

const { $lang, $track, $t } = useNuxtApp()

const corridor = inject('corridor') as Corridor
const getResultAnalyticsObj = inject('getResultAnalyticsObj') as (
  slug: string,
  location?: string,
) => ComparisonResult | undefined
const sectionAnalyticsObj = inject<Ref<ComparisonSection>>('sectionAnalyticsObj')
const comparisonAnalyticsObj = inject<Ref<Comparison>>('comparisonAnalyticsObj')

onMounted(async () => {
  await panelOpen(
    $track,
    'External Fees Details',
    comparisonAnalyticsObj?.value,
    sectionAnalyticsObj?.value,
    getResultAnalyticsObj(props.provider.slug),
  )
})

const bankPayin = computed(() => {
  return props.quote.payin === 'INTERNATIONAL_BANK' && props.quote.fee.externalPayin
})

const bankPayout = computed(() => {
  return props.quote.payout === 'INTERNATIONAL_BANK' && props.quote.fee.externalPayout
})

const payinFee = computed(() => {
  return formatMoney(props.quote.fee.externalPayin, corridor.currencyFrom.iso, $lang)
})

const payoutFee = computed(() => {
  return formatMoney(props.quote.fee.externalPayout, corridor.currencyFrom.iso, $lang)
})

const receivedFee = computed(() => {
  const fee = props.quote.fee.externalPayout * props.quote.rate
  return formatMoney(fee, corridor.currencyTo.iso, $lang)
})

const totalFee = computed(() => {
  const fee = props.quote.fee.externalPayin + props.quote.fee.externalPayout
  return formatMoney(fee, corridor.currencyFrom.iso, $lang)
})

const description = computed(() => {
  if (bankPayin.value && bankPayout.value) {
    return $t('external_fees.description_in_out')
  }
  if (bankPayin.value) {
    return $t('external_fees.description_in')
  }
  if (bankPayout.value) {
    return $t('external_fees.description_out')
  }
  return $t('external_fees.description_other')
})

const descriptionReason = computed(() => {
  if (bankPayin.value && bankPayout.value) {
    return $t('external_fees.reason_bank_in_out', {
      provider: props.provider.displayName,
    })
  }
  if (bankPayin.value) {
    return $t('external_fees.reason_bank_in', {
      provider: props.provider.displayName,
    })
  }
  if (bankPayout.value) {
    return $t('external_fees.reason_bank_out', {
      provider: props.provider.displayName,
    })
  }
  return undefined
})
</script>
